import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { TreeView } from '@mui/lab';
import { Divider } from '@mui/material';
import { SyntheticEvent } from 'react';
import { NodeData } from 'reaflow';
import DraggableTreeItem from '../DraggableTreeItem';
import { IncludedNodes, SystemOverviewDto } from '../types';
import {
  satelliteNodeBackground,
  dishNodeBackground,
  analyzerNodeBackground,
  switchNodeBackground,
  locationNodeBackground,
} from '../utils/theming';

export const useSystemOverviewTree = (
  selectedNode: string,
  handleSelectNode: (event: SyntheticEvent, nodeIds: string[]) => void,
  systemOverview: SystemOverviewDto | undefined,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const includedNodes: IncludedNodes = {
    satellites: [],
    dishes: [],
    analyzers: [],
    switches: [],
    locations: [],
  };
  if (!systemOverview) return null;
  return (
    <TreeView
      defaultCollapseIcon={<ExpandMore />}
      defaultExpandIcon={<ChevronRight />}
      selected={[selectedNode]}
      onNodeSelect={handleSelectNode}
      sx={{ width: '100%' }}
    >
      {getSatelliteNodes(systemOverview, includedNodes, nodeLinker)}
      {getDishesNotLinkedToSatellites(
        systemOverview,
        includedNodes,
        nodeLinker
      )}
      {getSwitchesAndAnalyzersNotLinkedToDishes(
        systemOverview,
        includedNodes,
        nodeLinker
      )}
      <Divider sx={{ mt: '1rem', mb: '1rem' }} />
      {getUnlinkedSatelliteNodes(systemOverview, includedNodes, nodeLinker)}
      {getUnlinkedDishes(systemOverview, includedNodes, nodeLinker)}
      {getUnlinkedSwitches(systemOverview, includedNodes, nodeLinker)}
      {getAnalyzersNotLinkedToDishes(systemOverview, includedNodes, nodeLinker)}
      {getUnlinkedLocations(systemOverview, includedNodes, nodeLinker)}
    </TreeView>
  );
};

const getSatelliteNodes = (
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const satelliteId of Object.keys(systemOverview.satellites)) {
    const satellite = systemOverview.satellites[Number(satelliteId)];
    if (satellite.dishIds.length === 0) continue;
    includedNodes.satellites.push(Number(satelliteId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'satellite-' + satelliteId}
        key={'satellite-' + satelliteId}
        label={systemOverview.satellites[Number(satelliteId)].name}
        fontColor={satelliteNodeBackground}
        linkNodes={nodeLinker}
      >
        {getDishNodesForSatellite(
          satelliteId,
          systemOverview,
          includedNodes,
          nodeLinker
        )}
      </DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getUnlinkedSatelliteNodes = (
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const satelliteId of Object.keys(systemOverview.satellites)) {
    if (includedNodes.satellites.includes(Number(satelliteId))) continue;
    const satellite = systemOverview.satellites[Number(satelliteId)];
    if (satellite.dishIds.length !== 0) continue;
    includedNodes.satellites.push(Number(satelliteId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'satellite-' + satelliteId}
        key={'satellite-' + satelliteId}
        label={systemOverview.satellites[Number(satelliteId)].name}
        fontColor={satelliteNodeBackground}
        linkNodes={nodeLinker}
      ></DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getDishNodesForSatellite = (
  satelliteId: number | string,
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const dishId of systemOverview.satellites[Number(satelliteId)].dishIds) {
    includedNodes.dishes.push(Number(dishId));
    if (systemOverview.dishes[Number(dishId)].locationId)
      includedNodes.locations.push(
        Number(systemOverview.dishes[Number(dishId)].locationId)
      );
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'dish-' + dishId}
        key={'dish-' + dishId}
        label={systemOverview.dishes[Number(dishId)].name}
        fontColor={dishNodeBackground}
        linkNodes={nodeLinker}
      >
        {systemOverview.dishes[dishId].locationId && (
          <DraggableTreeItem
            nodeId={'location-' + systemOverview.dishes[dishId].locationId}
            key={'location-' + systemOverview.dishes[dishId].locationId}
            label={systemOverview.dishes[dishId].locationName || ''}
            fontColor={locationNodeBackground}
            linkNodes={nodeLinker}
          />
        )}
        {getSwitchesAndAnalyzersForDish(
          dishId,
          systemOverview,
          includedNodes,
          nodeLinker
        )}
        {getAnalyzersForDish(dishId, systemOverview, includedNodes, nodeLinker)}
      </DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getSwitchesAndAnalyzersForDish = (
  dishId: string | number,
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const switchId of systemOverview.dishes[Number(dishId)].switchIds) {
    includedNodes.switches.push(Number(switchId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'switch-' + switchId}
        key={'switch-' + switchId}
        label={systemOverview.switches[Number(switchId)].name}
        fontColor={switchNodeBackground}
        linkNodes={nodeLinker}
      >
        {getAnalyzersForSwitch(
          switchId,
          systemOverview,
          includedNodes,
          nodeLinker
        )}
      </DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getAnalyzersForSwitch = (
  switchId: string | number,
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const analyzerId of systemOverview.switches[Number(switchId)]
    .analyzerIds) {
    includedNodes.analyzers.push(Number(analyzerId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'analyzer-' + analyzerId}
        key={'analyzer-' + analyzerId}
        label={systemOverview.analyzers[Number(analyzerId)].name}
        fontColor={analyzerNodeBackground}
        linkNodes={nodeLinker}
      ></DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getAnalyzersForDish = (
  dishId: number | string,
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const analyzerId of systemOverview.dishes[Number(dishId)].analyzerIds) {
    includedNodes.analyzers.push(Number(analyzerId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'analyzer-' + analyzerId}
        key={'analyzer-' + analyzerId}
        label={systemOverview.analyzers[Number(analyzerId)].name}
        fontColor={analyzerNodeBackground}
        linkNodes={nodeLinker}
      ></DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getDishesNotLinkedToSatellites = (
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const dishId of Object.keys(systemOverview.dishes)) {
    if (includedNodes.dishes.includes(Number(dishId))) continue;
    const dish = systemOverview.dishes[Number(dishId)];
    if (
      dish.analyzerIds.length === 0 &&
      !dish.locationId &&
      dish.switchIds.length === 0 &&
      !dish.satelliteId
    )
      continue;
    includedNodes.dishes.push(Number(dishId));
    if (systemOverview.dishes[Number(dishId)].locationId)
      includedNodes.locations.push(
        Number(systemOverview.dishes[Number(dishId)].locationId)
      );
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'dish-' + dishId}
        key={'dish-' + dishId}
        label={systemOverview.dishes[Number(dishId)].name}
        fontColor={dishNodeBackground}
        linkNodes={nodeLinker}
      >
        {systemOverview.dishes[Number(dishId)].locationId && (
          <DraggableTreeItem
            nodeId={
              'location-' + systemOverview.dishes[Number(dishId)].locationId
            }
            key={'location-' + systemOverview.dishes[Number(dishId)].locationId}
            label={systemOverview.dishes[Number(dishId)].locationName || ''}
            fontColor={locationNodeBackground}
            linkNodes={nodeLinker}
          />
        )}
        {getSwitchesAndAnalyzersForDish(
          dishId,
          systemOverview,
          includedNodes,
          nodeLinker
        )}
        {getAnalyzersForDish(dishId, systemOverview, includedNodes, nodeLinker)}
      </DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getUnlinkedDishes = (
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const dishId of Object.keys(systemOverview.dishes)) {
    if (includedNodes.dishes.includes(Number(dishId))) continue;
    const dish = systemOverview.dishes[Number(dishId)];
    if (
      dish.analyzerIds.length !== 0 ||
      dish.locationId ||
      dish.switchIds.length !== 0 ||
      dish.satelliteId
    )
      continue;
    includedNodes.dishes.push(Number(dishId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'dish-' + dishId}
        key={'dish-' + dishId}
        label={systemOverview.dishes[Number(dishId)].name}
        fontColor={dishNodeBackground}
        linkNodes={nodeLinker}
      ></DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getSwitchesAndAnalyzersNotLinkedToDishes = (
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const switchId of Object.keys(systemOverview.switches)) {
    if (includedNodes.switches.includes(Number(switchId))) continue;
    const switchItem = systemOverview.switches[Number(switchId)];
    if (switchItem.analyzerIds.length === 0) continue;
    includedNodes.switches.push(Number(switchId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'switch-' + switchId}
        key={'switch-' + switchId}
        label={systemOverview.switches[Number(switchId)].name}
        fontColor={switchNodeBackground}
        linkNodes={nodeLinker}
      >
        {getAnalyzersForSwitch(
          switchId,
          systemOverview,
          includedNodes,
          nodeLinker
        )}
      </DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getUnlinkedSwitches = (
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const switchId of Object.keys(systemOverview.switches)) {
    if (includedNodes.switches.includes(Number(switchId))) continue;
    const switchItem = systemOverview.switches[Number(switchId)];
    if (switchItem.analyzerIds.length !== 0) continue;
    includedNodes.switches.push(Number(switchId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'switch-' + switchId}
        key={'switch-' + switchId}
        label={systemOverview.switches[Number(switchId)].name}
        fontColor={switchNodeBackground}
        linkNodes={nodeLinker}
      ></DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getAnalyzersNotLinkedToDishes = (
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const analyzerId of Object.keys(systemOverview.analyzers)) {
    if (includedNodes.analyzers.includes(Number(analyzerId))) continue;
    includedNodes.analyzers.push(Number(analyzerId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'analyzer-' + analyzerId}
        key={'analyzer-' + analyzerId}
        label={systemOverview.analyzers[Number(analyzerId)].name}
        fontColor={analyzerNodeBackground}
        linkNodes={nodeLinker}
      ></DraggableTreeItem>
    );
  }
  return nodesToReturn;
};

const getUnlinkedLocations = (
  systemOverview: SystemOverviewDto,
  includedNodes: IncludedNodes,
  nodeLinker: (from: NodeData<any>, to: NodeData<any>) => void
) => {
  const nodesToReturn: JSX.Element[] = [];
  for (const locationId of Object.keys(systemOverview.locations)) {
    if (includedNodes.locations.includes(Number(locationId))) continue;
    includedNodes.locations.push(Number(locationId));
    nodesToReturn.push(
      <DraggableTreeItem
        nodeId={'location-' + locationId}
        key={'location-' + locationId}
        label={systemOverview.locations[Number(locationId)].name}
        fontColor={locationNodeBackground}
        linkNodes={nodeLinker}
      ></DraggableTreeItem>
    );
  }
  return nodesToReturn;
};
